import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { formatGameTime } from "@/features/analysis";
import { formatPercent } from "@/features/matches";
import { cn } from "@/shared/lib";

import { useDrillAssets } from "../../api/useDrillAssets";
import type { DrillAnswer, DrillSpec } from "../../model/Drill";
import { AssetImage } from "./AssetImage";
import { OptionButton } from "./OptionButton";
import { optionState } from "./optionState";
import { useOptionView } from "./useOptionView";

// Без точных минут: бэкенд берёт предметы, которые сильные покупают в этот отрезок матча
// с запасом в пару минут до первой покупки на шкале.
const USUALLY = msg({ message: "Around this time strong players on {hero} usually buy" });
const STRONG_SHARE = msg({ message: "{share} of strong players, around {time}" });

type PurchaseTimelineProps = {
  spec: DrillSpec;
  answer: DrillAnswer;
  graded: boolean;
  locked: boolean;
  onPick: (answer: DrillAnswer) => void;
};

// Шкала покупок матча: иконка и время каждой покупки по порядку. Нажатие — ответ.
// Над шкалой — что сильные на герое обычно покупают в этот отрезок матча (`references`,
// макет «4е» `design/prototype/DrillFixes.dc.html`): с этой строкой видно, какая покупка
// не из сборки. После ответа у каждой покупки — доля сильных и их обычное время.
// На узком экране шкала прокручивается вбок внутри блока.
export const PurchaseTimeline = ({
  spec,
  answer,
  graded,
  locked,
  onPick,
}: PurchaseTimelineProps) => {
  const { i18n } = useLingui();
  const view = useOptionView();
  const assets = useDrillAssets();
  const hero = assets.hero(spec.hero_id).name;
  return (
    <div className="flex flex-col gap-3">
      {spec.references.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 rounded-xl border border-border bg-surface px-3.5 py-3">
          <span className="text-sm text-foreground-soft">
            {i18n._({ ...USUALLY, values: { hero } })}
          </span>
          <ul className="flex flex-wrap gap-2">
            {spec.references.map((reference) => {
              const referenceView = view(reference);
              const share = formatPercent(reference.value ?? 0, i18n.locale);
              return (
                <li
                  key={reference.id}
                  title={`${referenceView.name}, ${share}`}
                  className="flex w-14 flex-col items-center gap-0.5"
                >
                  {referenceView.asset !== null && (
                    <AssetImage asset={referenceView.asset} size={36} />
                  )}
                  <span className="sr-only">{referenceView.name}</span>
                  <span className="font-mono text-[11px] text-subtle">{share}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <ol className="flex gap-2 overflow-x-auto pb-2">
        {spec.options.map((option) => {
          const optionView = view(option);
          const picked = answer.option_ids.includes(option.id);
          const correct = spec.correct_ids.includes(option.id);
          const strong =
            graded && option.pick_rate != null
              ? i18n._({
                  ...STRONG_SHARE,
                  values: {
                    share: formatPercent(option.pick_rate, i18n.locale),
                    time: option.time_s == null ? "—" : formatGameTime(option.time_s),
                  },
                })
              : null;
          return (
            <li key={option.id} className="shrink-0">
              <OptionButton
                state={optionState(
                  picked,
                  correct,
                  graded,
                  false,
                  spec.close_ids.includes(option.id),
                )}
                pressed={picked}
                locked={locked}
                label={`${optionView.name}, ${formatGameTime(option.value ?? 0)}`}
                onClick={() => {
                  onPick({ ...answer, option_ids: [option.id] });
                }}
                className="w-24 flex-col justify-start gap-1.5 px-2 text-center"
              >
                {optionView.asset !== null && (
                  <AssetImage asset={optionView.asset} size={44} inOption />
                )}
                <span className="line-clamp-2 text-xs leading-tight">{optionView.name}</span>
                <span className="font-mono text-xs text-subtle">
                  {formatGameTime(option.value ?? 0)}
                </span>
                {strong !== null && (
                  <span
                    className={cn(
                      "text-[11px] leading-tight",
                      correct ? "font-semibold text-bad" : "text-muted",
                    )}
                  >
                    {strong}
                  </span>
                )}
              </OptionButton>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
